/**
 * Blog helpers shared by the blog pages and the server's on-request blog HTML
 * (build F1/F17). Pure functions; safe to import on the server.
 */
import type { BlogPost } from "@shared/schema";
import { getCorePage, type CorePage } from "@/data/core-pages";
import { marketingTitle } from "@/data/marketing-seo";

export const BLOG_SITE_URL = "https://padpulse.io";

type DateLike = Date | string | null | undefined;

function toDate(value: DateLike): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function blogPostPath(post: Pick<BlogPost, "slug" | "id">): string {
  return `/blog/${post.slug || post.id}`;
}

/** The date a post shows: its publish date, else when it was created. */
export function blogPostDate(post: Pick<BlogPost, "publishedAt" | "createdAt">): Date | null {
  return toDate(post.publishedAt as DateLike) ?? toDate(post.createdAt as DateLike);
}

export function blogPostModified(post: Pick<BlogPost, "updatedAt" | "publishedAt" | "createdAt">): Date | null {
  return toDate(post.updatedAt as DateLike) ?? blogPostDate(post);
}

export function formatBlogDate(date: Date | null): string {
  return date ? date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }) : "";
}

/** Author shown on a post, or null to hide the byline (never an invented name). */
export function blogPostAuthor(post: Pick<BlogPost, "author">): string | null {
  const author = String(post.author ?? "").trim();
  return author || null;
}

/** The core page a post links to at the end, if it names one. */
export function blogPostCorePage(post: Pick<BlogPost, "corePage">): CorePage | null {
  return post.corePage ? getCorePage(String(post.corePage)) ?? null : null;
}

export function blogPostDescription(post: Pick<BlogPost, "metaDescription" | "excerpt" | "title">): string {
  return String(post.metaDescription || post.excerpt || post.title).slice(0, 300);
}

export function blogPostTitle(post: Pick<BlogPost, "title">): string {
  return marketingTitle(post.title);
}

/** BlogPosting structured data (build F2). */
export function blogPostingJsonLd(post: BlogPost): Record<string, unknown> {
  const url = `${BLOG_SITE_URL}${blogPostPath(post)}`;
  const published = blogPostDate(post);
  const modified = blogPostModified(post);
  const author = blogPostAuthor(post);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: blogPostDescription(post),
    image: post.imageUrl || `${BLOG_SITE_URL}/og-image.jpg`,
    ...(published ? { datePublished: published.toISOString() } : {}),
    ...(modified ? { dateModified: modified.toISOString() } : {}),
    ...(author ? { author: { "@type": "Person", name: author } } : {}),
    publisher: {
      "@type": "Organization",
      name: "PadPulse",
      logo: { "@type": "ImageObject", url: `${BLOG_SITE_URL}/favicon-512x512.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
  };
}
